import {
  DIAS_SEMANA,
  ETIQUETA_DIA,
  type DiaSemana,
  type RepartoDia,
  type SemanaRepartos,
} from '../../hooks/useCalendarioSemana'

/**
 * B.1 — Calendario en vista de UNA semana.
 *
 * El objetivo del bloque B.1 es que, desde una sola pantalla, se vea la carga de
 * TODOS los dias a la vez, sin cambiar de dia: siete columnas en escritorio,
 * siete filas apiladas en movil. Antes habia que entrar dia a dia para saber
 * cuanto habia agendado, y eso escondia los dias sobrecargados.
 *
 * Es un componente PRESENTACIONAL: recibe los repartos por props y no sabe nada
 * de la API. Asi se prueba sin red y la misma vista sirve tanto con el hook
 * `useCalendarioSemana` como con datos de una carga masiva ya en memoria.
 *
 * Al pulsar un dia se invoca `onSeleccionarDia`: la pantalla que lo use abrira
 * ahi el formulario de reparto. Aqui NO se implementa ese formulario a
 * proposito; el contrato es solo "me han pedido rellenar el dia X".
 */

/**
 * De donde sale el color de cada dia (B.2).
 *
 * El modulo de colores lo construye OTRO subagente en
 * `src/paginas/agendamiento/coloresDia.ts` y expondra `colorDeDia(dia)`. Para no
 * bloquearnos ni pisar ese fichero, `colorDeDia` entra por props como
 * dependencia inyectable, con un stub neutro por defecto.
 *
 * TODO(integracion B.2): cuando `coloresDia.ts` exista, la pagina que monte este
 * componente le pasara `colorDeDia={colorDeDia}` importado de alli. La firma
 * acordada es `(dia: DiaSemana) => string` y debe devolver clases de utilidad
 * del tema (tokens `bg-*`/`text-*`/`ring-*`), nunca hex sueltos.
 */
export type ColorDeDia = (dia: DiaSemana) => string

/**
 * Stub local mientras no hay modulo de colores: un color neutro del tema, igual
 * para los siete dias. No inventa hex; usa los tokens que ya existen. Se
 * sustituye por la funcion real por props.
 */
const colorNeutro: ColorDeDia = () => 'bg-superficie text-tinta-2 ring-borde'

interface Props {
  /** Los repartos de cada dia. Los siete dias siempre presentes (ver `semanaVacia`). */
  repartosPorDia: SemanaRepartos
  /** Se pulsa un dia para rellenar un reparto en el. */
  onSeleccionarDia: (dia: DiaSemana) => void
  /** Color por dia (B.2). Inyectable; por defecto, neutro. */
  colorDeDia?: ColorDeDia
  /** Texto opcional de la semana que se esta viendo (p. ej. "Semana del 12 al 18 de enero"). */
  tituloSemana?: string
}

/** Una tarjeta de reparto dentro de la columna de un dia. */
function TarjetaReparto({ reparto }: { reparto: RepartoDia }) {
  return (
    <li className="rounded-md border border-borde-suave bg-lienzo px-2 py-1.5 text-left">
      <p className="truncate text-sm font-medium text-tinta">{reparto.cliente}</p>
      <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-tinta-apagada">
        {reparto.ruta ? <span className="uppercase">{reparto.ruta}</span> : null}
        {reparto.resumen ? <span className="truncate">{reparto.resumen}</span> : null}
      </p>
    </li>
  )
}

/** La columna (o fila, en movil) de un dia: cabecera con la carga y sus repartos. */
function ColumnaDia({
  dia,
  repartos,
  onSeleccionarDia,
  clasesColor,
}: {
  dia: DiaSemana
  repartos: RepartoDia[]
  onSeleccionarDia: (dia: DiaSemana) => void
  clasesColor: string
}) {
  const carga = repartos.length

  return (
    <section
      aria-label={`${ETIQUETA_DIA[dia]}, ${carga} ${carga === 1 ? 'reparto' : 'repartos'}`}
      className="flex min-w-0 flex-col gap-2"
    >
      {/* La cabecera del dia es el boton: pulsarla es "rellenar un reparto en
          este dia". El color (B.2) tine SOLO la cabecera, no las tarjetas, para
          que el dato de cada reparto no dependa del color -hay operadores que no
          los distinguen- y la carga se lea igual en blanco y negro. */}
      <button
        type="button"
        onClick={() => onSeleccionarDia(dia)}
        aria-label={`Agendar un reparto el ${ETIQUETA_DIA[dia]}`}
        className={[
          'flex items-center justify-between gap-2 rounded-lg px-3 py-2 ring-1 transition-colors',
          'hover:brightness-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco',
          clasesColor,
        ].join(' ')}
      >
        <span className="text-sm font-semibold">{ETIQUETA_DIA[dia]}</span>
        {/* La carga en numero, siempre, aunque sea cero: un dia sin repartos es
            un dato (hay hueco), no un fallo de carga. */}
        <span className="shrink-0 text-xs font-medium tabular-nums">{carga}</span>
      </button>

      {carga === 0 ? (
        <p className="px-1 text-xs text-tinta-apagada">Sin repartos</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {repartos.map((reparto) => (
            <TarjetaReparto key={reparto.id} reparto={reparto} />
          ))}
        </ul>
      )}
    </section>
  )
}

export function CalendarioSemana({
  repartosPorDia,
  onSeleccionarDia,
  colorDeDia = colorNeutro,
  tituloSemana,
}: Props) {
  return (
    <div className="flex flex-col gap-3">
      {tituloSemana ? (
        <h2 className="text-sm font-medium text-tinta-apagada">{tituloSemana}</h2>
      ) : null}

      {/* Una sola cuadricula con los siete dias: es la esencia de B.1, ver la
          semana entera sin cambiar de dia. En movil se apilan (una columna) para
          no obligar a desplazarse en horizontal; desde `sm` van los siete en
          fila. `items-start` para que cada columna crezca hacia abajo con sus
          repartos sin estirar a las vecinas. */}
      <div
        role="list"
        aria-label="Repartos de la semana por día"
        className="grid grid-cols-1 items-start gap-3 sm:grid-cols-7"
      >
        {DIAS_SEMANA.map((dia) => (
          <div role="listitem" key={dia}>
            <ColumnaDia
              dia={dia}
              repartos={repartosPorDia[dia]}
              onSeleccionarDia={onSeleccionarDia}
              clasesColor={colorDeDia(dia)}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
